import { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import {
  devStaffLogin,
  devStaffLoginAvailable,
  requestDevStaffCode,
} from '../../lib/api/devStaffLogin';
import { clubSession as appClubSession } from '../../lib/auth';
import { formatOtpCodeInput } from '../auth/otpCode';
import { useT } from '../../i18n';
import { useRetryWait } from '../ui/useRetryWait';

// ─────────────────────────────────────────────────────────────────────
// THE DEV-ONLY SIGN-IN FORM ON `/sa` (#445).
//
// RENDERS NOTHING UNLESS THE DEPLOYMENT OFFERS IT. The endpoint is mounted
// only where `DEV_STAFF_LOGIN=enabled` and `/config` reports that, so the
// portal never shows a control that would answer 404 — one control, on the
// server, rather than a build flag that could drift from it.
//
// IT AUTHENTICATES; IT DOES NOT AUTHORIZE. The session it adopts is an
// ordinary one, and `/sa` applies the same platform RBAC to it as to an
// Entra session. An account without a platform role signs in here and sees
// exactly what it is entitled to, which is nothing.
//
// `adopt`, NOT a second session mechanism: the token is real and minted by
// the same issuer as login, and what this skips is only the credential step
// the endpoint already did — the same path the dev personas use.
//
// ── TWO STEPS NOW: AN ADDRESS, THEN A CODE (#754 stage 2) ─────────
//
// NOT `AccountCodeSignIn`, deliberately. That component is the CUSTOMER
// entrance and is wired to `/auth/otp`, the door that provisions an account
// for any address — pointing it at this panel would mean giving it a
// switchable transport, and a mis-set switch there is a path into `/sa`
// through the door that admits everybody. Two doors that must not be confused
// are better kept as two, and what is duplicated is a hundred lines of form
// state, not a rule.
//
// IT SAYS NO MORE THAN THE SERVER DOES. The request answers the same 202 for
// an unknown address, the real one and a disabled account, so this moves to
// the code step either way and the notice is conditional («if that account
// exists»). A form that only advanced for the real address would be the
// address oracle the endpoint refuses to be.
// ─────────────────────────────────────────────────────────────────────

export interface DevStaffLoginFormProps {
  /** Injectable for tests. */
  available?: typeof devStaffLoginAvailable;
  requestCode?: typeof requestDevStaffCode;
  login?: typeof devStaffLogin;
  clubSession?: Pick<typeof appClubSession, 'adopt'>;
}

/**
 * A STRING discriminant, not a boolean.
 *
 * This repository compiles with `strict: false`, under which a boolean literal
 * union does not narrow — `step === 'code'` does, and the busy states have to
 * be distinguishable from the idle ones to label the button.
 */
type Step = 'email' | 'sending' | 'code' | 'verifying';

export function DevStaffLoginForm({
  available = devStaffLoginAvailable,
  requestCode = requestDevStaffCode,
  login = devStaffLogin,
  clubSession = appClubSession,
}: DevStaffLoginFormProps) {
  const t = useT();
  const [offered, setOffered] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('email');
  const [notice, setNotice] = useState<string | null>(null);
  // The real wait, counted down, with the submit held until it passes (#778
  // Q7) — the same family of sentences every other throttled door uses.
  const wait = useRetryWait<'send' | 'verify'>();
  const [sentTo, setSentTo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void available().then((yes) => {
      if (!cancelled) setOffered(yes);
    });
    return () => {
      cancelled = true;
    };
  }, [available]);

  if (!offered) return null;

  const busy = step === 'sending' || step === 'verifying';
  const onCodeStep = step === 'code' || step === 'verifying';

  /** ONE MESSAGE FOR EVERY REFUSAL. See the header. A throttle is the wait. */
  const messageFor = (state: 'refused' | 'unavailable'): string =>
    state === 'refused' ? t('devLogin.refused') : t('devLogin.unavailable');

  const send = async () => {
    if (wait.waiting('send')) return;
    wait.release('send');
    setStep('sending');
    setNotice(null);
    const out = await requestCode(email);
    if (out.state === 'sent') {
      setSentTo(email);
      setStep('code');
      return;
    }
    setStep('email');
    if (out.state === 'throttled') wait.start('send', out.retryAfterSeconds);
    else setNotice(messageFor(out.state));
  };

  const signIn = async () => {
    if (wait.waiting('verify')) return;
    wait.release('verify');
    setStep('verifying');
    setNotice(null);
    const out = await login(email, code);
    if (out.state === 'ok') {
      await clubSession.adopt(out.session);
      return;
    }
    setStep('code');
    if (out.state === 'throttled') wait.start('verify', out.retryAfterSeconds);
    else setNotice(messageFor(out.state));
  };
  const shownNotice = notice ?? wait.notice;

  return (
    <form
      className="rounded-xl border border-dashed border-line bg-bg-soft/40 p-5 h-full"
      data-testid="sa-dev-login"
      onSubmit={(e) => {
        e.preventDefault();
        void (onCodeStep ? signIn() : send());
      }}
    >
      <div className="flex items-center gap-2">
        <KeyRound size={16} className="text-ink-3" aria-hidden="true" />
        <h2 className="font-display text-base font-semibold text-ink">{t('devLogin.title')}</h2>
      </div>
      <p className="text-xs text-ink-3 mt-1">{t('devLogin.explainer')}</p>
      <div className="mt-4 flex flex-col gap-3">
        {!onCodeStep && (
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
              {t('devLogin.email')}
            </span>
            <input
              className="input w-full text-sm"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="sa-dev-login-email"
            />
          </label>
        )}
        {onCodeStep && (
          <>
            <p className="text-xs text-ink-3" data-testid="sa-dev-login-sent">
              {t('devLogin.codeSent', { email: sentTo ?? email })}
            </p>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
                {t('devLogin.code')}
              </span>
              <input
                className="input w-full text-sm font-mono tracking-[0.3em]"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                // No `maxLength`: the cap lives in `formatOtpCodeInput`, after
                // the non-digits are stripped. See that file for why.
                onChange={(e) => setCode(formatOtpCodeInput(e.target.value))}
                data-testid="sa-dev-login-code"
              />
            </label>
          </>
        )}
        <button
          type="submit"
          className="btn-lime w-full justify-center py-2.5"
          disabled={busy || wait.waiting(onCodeStep ? 'verify' : 'send')}
          data-testid="sa-dev-login-submit"
        >
          {onCodeStep
            ? step === 'verifying'
              ? t('devLogin.submitting')
              : t('devLogin.submit')
            : step === 'sending'
              ? t('devLogin.sending')
              : t('devLogin.sendCode')}
        </button>
        {onCodeStep && (
          <button
            type="button"
            className="text-xs text-ink-3 underline"
            onClick={() => {
              setStep('email');
              setCode('');
              setNotice(null);
              wait.clear();
            }}
            data-testid="sa-dev-login-restart"
          >
            {t('devLogin.again')}
          </button>
        )}
      </div>
      {shownNotice && (
        <p
          role="alert"
          className="mt-3 text-xs text-rose px-3 py-2 rounded bg-rose/10 border border-rose/20"
          data-testid="sa-dev-login-notice"
        >
          {shownNotice}
        </p>
      )}
    </form>
  );
}
